import {
  ALL_DAYS_MIN_DAY_PX,
  COMPACT_DAY_LABEL_STRIDES,
  COMPACT_DAY_ROW_HEIGHT_PX,
  COMPACT_MIN_DAY_PX,
  DAY_MS,
  DAY_ROW_CLASS,
  DAY_ROW_HEIGHT_PX,
  DAYS_PER_WEEK,
  EDGE_PIXEL_TOLERANCE,
  LETTERS_MIN_DAY_PX,
  MAX_AXIS_DAYS,
  MONDAY,
  SATURDAY,
  SUNDAY,
  THINNED_DAY_LABEL_MIN_PX,
  THINNED_DAY_LABEL_STRIDES,
  WEEK_BAND_CLASS,
  WEEK_BAND_HEIGHT_PX,
  WEEK_CELL_CLASS,
  WEEK_STRIDE_EPOCH_MS,
  WEEK_TEXT_CHAR_PX,
  WEEK_TEXT_MIN_PX,
  WEEKDAY_CLASS,
  WEEKEND_CLASS,
} from './x-axis-label-tier.constants';
import {
  TieredLabelAxis,
  WeekCellHtmlParams,
  WeekendSpan,
  XAxisLabelOptions,
  XAxisLabelPlan,
  XAxisLabelTier,
  XAxisLabelTierTickPositioner,
  XAxisWeekCell,
} from './x-axis-label-tier.types';

const WEEK_MS = DAYS_PER_WEEK * DAY_MS;

export function startOfUtcDay(ms: number): number {
  return Math.floor(ms / DAY_MS) * DAY_MS;
}

export function firstUtcMidnightAtOrAfter(ms: number): number {
  return Math.ceil(ms / DAY_MS) * DAY_MS;
}

export function startOfUtcWeek(ms: number): number {
  const day = startOfUtcDay(ms);
  const offset =
    (new Date(day).getUTCDay() - MONDAY + DAYS_PER_WEEK) % DAYS_PER_WEEK;

  return day - offset * DAY_MS;
}

export function weekIndexOf(ms: number): number {
  return Math.round((startOfUtcWeek(ms) - WEEK_STRIDE_EPOCH_MS) / WEEK_MS);
}

export function pixelsPerDay(
  min: number,
  max: number,
  axisLengthPx: number
): number {
  return (axisLengthPx * DAY_MS) / (max - min);
}

export function resolveLabelTier(dayPx: number): XAxisLabelTier {
  if (dayPx >= ALL_DAYS_MIN_DAY_PX) {
    return XAxisLabelTier.AllDays;
  }

  if (dayPx >= COMPACT_MIN_DAY_PX) {
    return XAxisLabelTier.Compact;
  }

  return dayPx >= LETTERS_MIN_DAY_PX
    ? XAxisLabelTier.Letters
    : XAxisLabelTier.Weekly;
}

export function resolveWeekTextStride(dayPx: number): number {
  if (!Number.isFinite(dayPx) || dayPx <= 0) {
    return 1;
  }

  return Math.max(1, Math.ceil(WEEK_TEXT_MIN_PX / (DAYS_PER_WEEK * dayPx)));
}

/** Days between thinned labels; the widest stride if none gives enough room. */
export function resolveDayLabelStride(
  dayPx: number,
  labelWidthPx = THINNED_DAY_LABEL_MIN_PX,
  strides = THINNED_DAY_LABEL_STRIDES
): number {
  return (
    strides.find((stride) => stride * dayPx >= labelWidthPx) ??
    strides[strides.length - 1]
  );
}

/** Aligned on a fixed Monday, so labels stay on their days while panning and a 7-day stride lands on Mondays. */
export function isDayLabelDay(dayMs: number, stride: number): boolean {
  const dayIndex = Math.round((dayMs - WEEK_STRIDE_EPOCH_MS) / DAY_MS);

  return ((dayIndex % stride) + stride) % stride === 0;
}

export function buildDailyPositions(min: number, max: number): number[] {
  const positions: number[] = [];

  for (
    let day = firstUtcMidnightAtOrAfter(min);
    day <= max && positions.length < MAX_AXIS_DAYS;
    day += DAY_MS
  ) {
    positions.push(day);
  }

  return positions;
}

export function buildWeeklyPositions(min: number, max: number): number[] {
  const positions: number[] = [];
  const first = startOfUtcWeek(min);

  for (
    let week = first >= min ? first : first + WEEK_MS;
    week <= max && positions.length < MAX_AXIS_DAYS;
    week += WEEK_MS
  ) {
    positions.push(week);
  }

  if (positions.length) {
    return positions;
  }

  const fallback = firstUtcMidnightAtOrAfter(min);

  return fallback <= max ? [fallback] : [];
}

export function buildWeekCells(
  positions: number[]
): Map<number, XAxisWeekCell> {
  const cells = new Map<number, XAxisWeekCell>();

  positions.forEach((anchor, index) => {
    if (index > 0 && new Date(anchor).getUTCDay() !== MONDAY) {
      return;
    }

    const weekStart = startOfUtcWeek(anchor);

    cells.set(anchor, {
      anchor,
      dayCount: DAYS_PER_WEEK - (anchor - weekStart) / DAY_MS,
      weekStart,
      weekIndex: weekIndexOf(weekStart),
    });
  });

  return cells;
}

/**
 * Groups neighbouring weeks into blocks of `stride` weeks (aligned on the week index, so the
 * labels stay on their weeks while panning), so a label that is wider than one narrow week can
 * span the whole block. Only the block's first cell stays; the other weeks draw nothing of
 * their own. A block cut short at either end of the axis joins its neighbour instead of being
 * left without room for a label.
 */
export function mergeWeekCells(
  cells: Map<number, XAxisWeekCell>,
  stride: number
): Map<number, XAxisWeekCell> {
  if (stride <= 1) {
    return cells;
  }

  const blocks = new Map<number, XAxisWeekCell>();

  cells.forEach((cell) => {
    const key = Math.floor(cell.weekIndex / stride);
    const head = blocks.get(key);

    if (head) {
      head.dayCount += cell.dayCount;
      head.weekCount = (head.weekCount ?? 1) + 1;
      return;
    }

    blocks.set(key, { ...cell, weekCount: 1 });
  });

  const ordered = [...blocks.values()];
  const isShort = (block: XAxisWeekCell) => (block.weekCount ?? 1) < stride;

  if (ordered.length > 1 && isShort(ordered[0])) {
    const [first, second] = ordered;
    ordered.splice(0, 2, {
      ...first,
      dayCount: first.dayCount + second.dayCount,
      weekCount: (first.weekCount ?? 1) + (second.weekCount ?? 1),
    });
  }

  const last = ordered[ordered.length - 1];
  if (ordered.length > 1 && isShort(last)) {
    const previous = ordered[ordered.length - 2];
    previous.dayCount += last.dayCount;
    previous.weekCount = (previous.weekCount ?? 1) + (last.weekCount ?? 1);
    ordered.pop();
  }

  return new Map(ordered.map((block) => [block.anchor, block]));
}

export function buildWeekendSpans(min: number, max: number): WeekendSpan[] {
  const spans: WeekendSpan[] = [];

  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
    return spans;
  }

  const firstDay = startOfUtcDay(min);
  const lastDay = startOfUtcDay(max);

  for (
    let day = firstDay, guard = 0;
    day <= lastDay && guard < MAX_AXIS_DAYS;
    day += DAY_MS, guard++
  ) {
    if (new Date(day).getUTCDay() === SATURDAY) {
      spans.push({ from: day, to: day + 2 * DAY_MS });
    }
  }

  return spans;
}

export function buildXAxisLabelPlan(
  min: number,
  max: number,
  axisLengthPx: number
): XAxisLabelPlan | null {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
    return null;
  }

  if (!Number.isFinite(axisLengthPx) || axisLengthPx <= 0) {
    return null;
  }

  const dayPx = pixelsPerDay(min, max, axisLengthPx);
  const tier = resolveLabelTier(dayPx);
  const positions =
    tier === XAxisLabelTier.Weekly
      ? buildWeeklyPositions(min, max)
      : buildDailyPositions(min, max);

  if (!positions.length) {
    return null;
  }

  const weekTextStride = resolveWeekTextStride(dayPx);
  const cells = buildWeekCells(positions);

  return {
    tier,
    positions,
    // Only the weekly tier has weeks too narrow for their own label.
    cells:
      tier === XAxisLabelTier.Weekly
        ? mergeWeekCells(cells, weekTextStride)
        : cells,
    weekTextStride,
  };
}

function utcMonthShort(ms: number): string {
  return new Date(ms).toLocaleDateString('en-US', {
    month: 'short',
    timeZone: 'UTC',
  });
}

function utcWeekdayShort(ms: number): string {
  return new Date(ms)
    .toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' })
    .toUpperCase();
}

export function weekRangeText(
  weekStart: number,
  dayBeforeMonth: boolean,
  weekCount = 1
): string {
  const weekEnd = weekStart + (weekCount * DAYS_PER_WEEK - 1) * DAY_MS;
  const startDay = new Date(weekStart).getUTCDate();
  const endDay = new Date(weekEnd).getUTCDate();
  const startMonth = utcMonthShort(weekStart);
  const endMonth = utcMonthShort(weekEnd);

  if (startMonth === endMonth) {
    return dayBeforeMonth
      ? `${startDay} - ${endDay} ${startMonth}`
      : `${startMonth} ${startDay} - ${endDay}`;
  }

  return dayBeforeMonth
    ? `${startDay} ${startMonth} - ${endDay} ${endMonth}`
    : `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
}

export function dayCellText(
  dayMs: number,
  tier: XAxisLabelTier,
  dayBeforeMonth: boolean
): { date: string; day: string } {
  const dayOfMonth = new Date(dayMs).getUTCDate();
  const weekday = utcWeekdayShort(dayMs);

  if (tier === XAxisLabelTier.AllDays) {
    const month = utcMonthShort(dayMs);

    return {
      date: dayBeforeMonth
        ? `${dayOfMonth} ${month}`
        : `${month} ${dayOfMonth}`,
      day: weekday,
    };
  }

  if (tier === XAxisLabelTier.Compact) {
    return { date: `${dayOfMonth}`, day: weekday };
  }

  if (tier === XAxisLabelTier.Letters) {
    return { date: `${dayOfMonth}`, day: weekday.charAt(0) };
  }

  return { date: '', day: '' };
}

function buildDayCellsHtml(
  cell: XAxisWeekCell,
  tier: XAxisLabelTier,
  dayBeforeMonth: boolean,
  cellLeft: number,
  dayPx: number,
  plotLeft: number,
  plotRight: number,
  labelOptions: XAxisLabelOptions
): string {
  const { showWeekday = true } = labelOptions;
  const cells: string[] = [];

  for (let index = 0; index < cell.dayCount; index++) {
    const dayMs = cell.anchor + index * DAY_MS;
    const weekday = new Date(dayMs).getUTCDay();
    const backgroundClass =
      weekday === SATURDAY || weekday === SUNDAY
        ? WEEKEND_CLASS
        : WEEKDAY_CLASS;
    const dayLeft = cellLeft + index * dayPx;
    const dayRight = dayLeft + dayPx;
    // Sub-pixel slack: a padded range puts the edge days exactly on the plot edges.
    const hidden =
      dayLeft < plotLeft - EDGE_PIXEL_TOLERANCE ||
      dayRight > plotRight + EDGE_PIXEL_TOLERANCE;
    const { date, day } = dayCellText(dayMs, tier, dayBeforeMonth);

    cells.push(
      `<div class="${backgroundClass}"${
        hidden ? ' style="visibility: hidden;"' : ''
      }>${date ? `<span class="date">${date}</span>` : ''}${
        day && showWeekday ? `<span class="day">${day}</span>` : ''
      }</div>`
    );
  }

  return cells.join('');
}

export function buildWeekCellHtml({
  axis,
  plan,
  cell,
  dayBeforeMonth,
  labelOptions = {},
}: WeekCellHtmlParams): string {
  const { dayRowHeightPx = DAY_ROW_HEIGHT_PX } =
    labelOptions as XAxisLabelOptions;
  const anchorPx = axis.toPixels(cell.anchor, false);
  const dayPx = axis.toPixels(cell.anchor + DAY_MS, false) - anchorPx;

  if (!Number.isFinite(dayPx) || dayPx <= 0) {
    return '';
  }

  const cellLeft = anchorPx;
  const cellWidth = cell.dayCount * dayPx;
  const plotLeft = axis.toPixels(axis.min as number, false);
  const plotRight = axis.toPixels(axis.max as number, false);
  const clipLeft = Math.max(0, plotLeft - cellLeft);
  const clipRight = Math.max(0, cellLeft + cellWidth - plotRight);
  const visibleWidth = cellWidth - clipLeft - clipRight;
  const hasWeekBand = plan.tier !== XAxisLabelTier.AllDays;
  const weekText = weekRangeText(
    cell.weekStart,
    dayBeforeMonth,
    cell.weekCount ?? 1
  );
  const showWeekText = weekText.length * WEEK_TEXT_CHAR_PX <= visibleWidth;
  const height = dayRowHeightPx + (hasWeekBand ? WEEK_BAND_HEIGHT_PX : 0);
  const weekBand = hasWeekBand
    ? `<div class="${WEEK_BAND_CLASS}" style="height: ${WEEK_BAND_HEIGHT_PX}px; width: ${visibleWidth}px; margin-left: ${clipLeft}px;">${
        showWeekText ? weekText : ''
      }</div>`
    : '';

  return `
    <div class="${WEEK_CELL_CLASS}" style="width: ${cellWidth}px; height: ${height}px; clip-path: inset(0px ${clipRight}px 0px ${clipLeft}px);">
      ${weekBand}
      <div class="${DAY_ROW_CLASS}" style="height: ${dayRowHeightPx}px;">${buildDayCellsHtml(
    cell,
    plan.tier,
    dayBeforeMonth,
    cellLeft,
    dayPx,
    plotLeft,
    plotRight,
    labelOptions
  )}</div>
    </div>
  `;
}

/** One clipping boundary for the entire compact header; week cells never own dates. */
export function buildCompactXAxisHtml(
  axis: WeekCellHtmlParams['axis'],
  plan: XAxisLabelPlan,
  dayBeforeMonth: boolean,
  {
    dayRowHeightPx = COMPACT_DAY_ROW_HEIGHT_PX,
    dayLabelInsetLeftPx = 0,
  }: XAxisLabelOptions = {}
): string {
  const min = axis.min as number;
  const max = axis.max as number;
  const left = axis.toPixels(min, false);
  const width = axis.toPixels(max, false) - left;
  if (!Number.isFinite(width) || width <= 0 || max <= min) {
    return '';
  }

  const dayPx = pixelsPerDay(min, max, width);
  const fullDate = dayPx >= ALL_DAYS_MIN_DAY_PX;
  // As the renewables day cells: every day gets its own label from the letters tier up.
  const labelWidth =
    dayPx >= LETTERS_MIN_DAY_PX ? dayPx : THINNED_DAY_LABEL_MIN_PX;
  const stride = resolveDayLabelStride(
    dayPx,
    labelWidth,
    COMPACT_DAY_LABEL_STRIDES
  );
  const backgrounds: string[] = [];
  const dates: string[] = [];
  const x = (time: number) => axis.toPixels(time, false) - left;

  for (
    let day = startOfUtcDay(min), guard = 0;
    day < max && guard < MAX_AXIS_DAYS;
    day += DAY_MS, guard++
  ) {
    const weekday = new Date(day).getUTCDay();
    const background =
      weekday === SATURDAY || weekday === SUNDAY
        ? WEEKEND_CLASS
        : WEEKDAY_CLASS;
    const from = Math.max(0, x(day));
    const to = Math.min(width, x(day + DAY_MS));
    backgrounds.push(
      `<div class="${background}" style="position: absolute; left: ${from}px; width: ${
        to - from
      }px; height: 100%;"></div>`
    );
    if (!isDayLabelDay(day, stride)) {
      continue;
    }
    // A day is labelled when the whole day is on the plot, clear of the unit.
    if (
      x(day) < dayLabelInsetLeftPx - EDGE_PIXEL_TOLERANCE ||
      x(day + DAY_MS) > width + EDGE_PIXEL_TOLERANCE
    ) {
      continue;
    }
    // A label wider than its day is nudged inward, never under the unit or past the right edge.
    const labelLeft = Math.min(
      Math.max(x(day + DAY_MS / 2) - labelWidth / 2, dayLabelInsetLeftPx),
      width - labelWidth
    );
    const text = fullDate
      ? dayCellText(day, XAxisLabelTier.AllDays, dayBeforeMonth).date
      : `${new Date(day).getUTCDate()}`;
    dates.push(
      `<span class="date" style="position: absolute; left: ${labelLeft}px; width: ${labelWidth}px; height: 100%; display: flex; align-items: center; justify-content: center;">${text}</span>`
    );
  }

  const bands: string[] = [];
  const hasWeekBand = plan.tier !== XAxisLabelTier.AllDays;
  if (hasWeekBand) {
    const weeks = plan.weekTextStride;
    const firstWeek = startOfUtcWeek(min);
    const weekOffset = ((weekIndexOf(min) % weeks) + weeks) % weeks;
    for (
      let week = firstWeek - weekOffset * WEEK_MS, guard = 0;
      week < max && guard < MAX_AXIS_DAYS;
      week += weeks * WEEK_MS, guard++
    ) {
      const from = Math.max(0, x(week));
      const to = Math.min(width, x(week + weeks * WEEK_MS));
      const text = weekRangeText(week, dayBeforeMonth, weeks);
      bands.push(
        `<div class="${WEEK_BAND_CLASS}" style="position: absolute; left: ${from}px; width: ${
          to - from
        }px; height: ${WEEK_BAND_HEIGHT_PX}px;">${
          text.length * WEEK_TEXT_CHAR_PX <= to - from ? text : ''
        }</div>`
      );
    }
  }
  const bandHeight = hasWeekBand ? WEEK_BAND_HEIGHT_PX : 0;
  // The formatter runs on the first tick, which can be later than the visible range's start.
  const offset = left - axis.toPixels(plan.positions[0], false);
  return `<div style="position: relative; left: ${offset}px; width: ${width}px; height: ${
    bandHeight + dayRowHeightPx
  }px; overflow: hidden;">${bands.join(
    ''
  )}<div class="${DAY_ROW_CLASS}" style="position: absolute; top: ${bandHeight}px; width: 100%; height: ${dayRowHeightPx}px;">${backgrounds.join(
    ''
  )}${dates.join('')}</div></div>`;
}

export const X_AXIS_LABEL_TIER_TICK_POSITIONER: XAxisLabelTierTickPositioner =
  function () {
    const axis = this as TieredLabelAxis & { len?: number };
    const plan = buildXAxisLabelPlan(
      axis.min as number,
      axis.max as number,
      axis.len ?? 0
    );

    axis.labelTierPlan = plan;

    return plan?.positions;
  };
